import PropTypes from "prop-types";
import { useEffect } from "react";
import { useRoutes } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import Themeroutes from "./routes/Router";
import "./assets/scss/style.scss";
import ToastContainer from "./components/toasts/ToastContainer";

import AlertProvider from "./components/alertProvider/AlertProvider";

import { useDispatch } from "react-redux";
import { connectToWs } from "./store/app/ws/WebSocketSlice";
import { fetchTypesWhereImOwenerOrDelegate } from "./store/app/DataQualityStatus/DataQualitySlice";
import useAuth from "./components/authGuard/useAuth";

/*    Handle exception version boundary */
import ErrorGeneric from "./views/ErrorGeneric";

import FilterSyncer from "./components/filterSyncer/FilterSyncer";

function ErrorFallback({ error }) {
  return (
    <ErrorGeneric
      code={error.message}
      title={error.message}
      message={error.message}
    />
  );
}

ErrorFallback.propTypes = {
  error: PropTypes.shape({
    message: PropTypes.any,
  }),
};

function App() {
  const routing = useRoutes(Themeroutes);
  const dispatch = useDispatch();
  const { isAuthorized } = useAuth();

  useEffect(() => {
    const forceReloadOnce = (reason) => {
      const key = "app-force-reload-done";
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, reason || "1");

      // Resetta anche il fingerprint così al reload successivo lo ricalcola
      sessionStorage.removeItem("app-build-fingerprint");

      const url = new URL(globalThis.location.href);
      url.searchParams.set("__v", Date.now().toString());
      globalThis.location.replace(url.toString());
    };

    const handleBeforeUnload = () => {
      localStorage.setItem("isRefreshed", "true");
    };

    const handleLoad = () => {
      sessionStorage.removeItem("app-force-reload-done"); 
      const isRefreshed = localStorage.getItem("isRefreshed");
      if (isRefreshed === "true") {
        dispatch(connectToWs());
        localStorage.removeItem("isRefreshed");
      }
    };

    const isChunkError = (msg = "") =>
      msg.includes("ChunkLoadError") ||
      /Loading chunk \d+ failed/i.test(msg) ||
      msg.includes("Failed to fetch dynamically imported module");

    const onWindowError = (event) => {
      if (isChunkError(event?.message || "")) {
        forceReloadOnce("chunk-error");
      }
    };

    const onUnhandledRejection = (event) => {
      const msg =
        event?.reason?.message ||
        event?.reason?.toString?.() ||
        "";
      if (isChunkError(msg)) {
        forceReloadOnce("chunk-rejection");
      }
    };

    const checkBuildVersion = async () => {
      try {
        const res = await fetch(`/asset-manifest.json?t=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;

        const data = await res.json();

        // Usa gli entrypoints come fingerprint della build
        const entrypoints = data?.entrypoints;
        if (!entrypoints || entrypoints.length === 0) return;

        // Crea una stringa unica che rappresenta questa build
        const buildFingerprint = entrypoints.sort().join("|");

        const stored = sessionStorage.getItem("app-build-fingerprint");
        if (!stored) {
          // Prima visita: salva il fingerprint
          sessionStorage.setItem("app-build-fingerprint", buildFingerprint);
          return;
        }

        if (stored !== buildFingerprint) {
          // Build cambiata → reset e reload
          sessionStorage.removeItem("app-build-fingerprint");
          forceReloadOnce("build-fingerprint-mismatch");
        }
      } catch (error) {
        console.error("Error checking build version:", error);
      }; 
    };

    globalThis.addEventListener("beforeunload", handleBeforeUnload);
    globalThis.addEventListener("load", handleLoad);
    globalThis.addEventListener("error", onWindowError, true);
    globalThis.addEventListener("unhandledrejection", onUnhandledRejection);

    checkBuildVersion();

    return () => {
      globalThis.removeEventListener("beforeunload", handleBeforeUnload);
      globalThis.removeEventListener("load", handleLoad);
      globalThis.removeEventListener("error", onWindowError, true);
      globalThis.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, [dispatch]);

  useEffect(() => {
    if (isAuthorized) {
      console.log("User authorized, fetching types where I'm owner or delegate");
      dispatch(fetchTypesWhereImOwenerOrDelegate());
    }
  }, [isAuthorized, dispatch]);

  return (
    <div className="App">
      <FilterSyncer />
      <ErrorBoundary FallbackComponent={ErrorFallback}>{routing}</ErrorBoundary>
      <ToastContainer />
      <AlertProvider />
    </div>
  );
}

export default App;